import {
  ArrowUpRight,
  FileText,
  Pill,
  Link2,
  UserSquare,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  MessageSquare,
} from "lucide-react";

const DOCS = [
  { icon: FileText, label: "Certificat médical", count: "2 certificats", active: true },
  { icon: Pill, label: "Ordonnances", count: "1 certificat" },
  { icon: Link2, label: "Référence spécialiste", count: "4 certificats" },
  { icon: UserSquare, label: "Consultation", count: "3 certificats" },
];

function CardHeader({ title, action }: { title: string; action?: "dark" | "yellow" }) {
  return (
    <div className="flex items-start justify-between">
      <h3 className="text-base font-semibold">{title}</h3>
      <button
        className={
          "grid h-8 w-8 place-items-center rounded-full " +
          (action === "yellow" ? "bg-accent text-accent-foreground" : "bg-white/[0.06] text-foreground")
        }
      >
        <ArrowUpRight className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function MyDocuments() {
  return (
    <div className="card-surface rounded-2xl p-5">
      <CardHeader title="Mes documents" />
      <div className="mt-4 grid grid-cols-2 gap-3">
        {DOCS.map((d) => {
          const Icon = d.icon;
          return (
            <div
              key={d.label}
              className={
                "rounded-xl p-3 transition " +
                (d.active ? "bg-primary/10 ring-1 ring-primary/30" : "bg-white/[0.03] hover:bg-white/[0.06]")
              }
            >
              <div
                className={
                  "grid h-9 w-9 place-items-center rounded-lg " +
                  (d.active ? "bg-primary/20 text-primary" : "bg-white/5 text-muted-foreground")
                }
              >
                <Icon className="h-4 w-4" />
              </div>
              <div className="mt-3 text-sm font-medium leading-tight">{d.label}</div>
              <div className="text-[11px] text-muted-foreground">{d.count}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ThreeDCheck() {
  return (
    <div className="card-surface relative overflow-hidden rounded-2xl p-5">
      <CardHeader title="Contrôle 3D" />
      <div className="relative mt-3 h-56">
        <img
          src="https://images.unsplash.com/photo-1559757148-5c350d0d3c56?auto=format&fit=crop&w=600&q=80"
          alt="Poumons 3D"
          className="absolute inset-0 m-auto h-full w-auto object-contain opacity-90 mix-blend-screen"
        />
        <span className="absolute right-10 top-4 grid h-6 w-6 place-items-center rounded-full bg-accent/30 text-accent">!</span>
        <span className="absolute left-8 top-1/2 grid h-6 w-6 place-items-center rounded-full bg-accent/30 text-accent">!</span>
        <div className="absolute left-1/2 top-1/2 w-44 -translate-x-1/2 -translate-y-1/2 rounded-xl bg-card/90 p-3 text-center shadow-xl backdrop-blur">
          <div className="text-xs font-semibold">Anomalie détectée</div>
          <p className="mt-0.5 text-[10px] leading-tight text-muted-foreground">
            Indicateur pulmonaire — irrégularités légères.
          </p>
          <button className="mt-2 rounded-full bg-accent px-3 py-1 text-[10px] font-medium text-accent-foreground">
            Prendre consultation
          </button>
        </div>
        <div className="absolute right-2 top-8 flex flex-col gap-2">
          {[ZoomIn, ZoomOut, RotateCcw].map((I, i) => (
            <button key={i} className="grid h-8 w-8 place-items-center rounded-full bg-white/[0.06] text-muted-foreground">
              <I className="h-3.5 w-3.5" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function PersonalData() {
  return (
    <div className="card-surface rounded-2xl p-5">
      <CardHeader title="Données personnelles" />
      <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
        {[
          ["Nom", "Cécilia Yo"],
          ["Genre", "Féminin"],
          ["Âge", "32"],
        ].map(([k, v]) => (
          <div key={k}>
            <div className="text-muted-foreground">{k}</div>
            <div className="mt-0.5 text-sm font-medium">{v}</div>
          </div>
        ))}
      </div>
      <div className="mt-4">
        <div className="text-xs text-muted-foreground">Allergies</div>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {["Pénicilline", "Arachides", "Crevettes", "Piqûres d'abeille"].map((a) => (
            <span key={a} className="chip text-[10px]">{a}</span>
          ))}
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
        <div><div className="text-muted-foreground">Groupe sanguin</div><div className="text-sm font-medium">O+</div></div>
        <div><div className="text-muted-foreground">Poids</div><div className="text-sm font-medium">58 kg</div></div>
      </div>
      <div className="mt-4 text-xs">
        <div className="text-muted-foreground">Taille</div>
        <div className="relative mt-2 h-10">
          <svg viewBox="0 0 200 30" className="h-full w-full">
            {Array.from({ length: 60 }).map((_, i) => {
              const isMid = i > 25 && i < 35;
              return <line key={i} x1={i * 3.4} y1={isMid ? 5 : 12} x2={i * 3.4} y2={isMid ? 25 : 18} stroke={isMid ? "oklch(0.78 0.18 150)" : "oklch(1 0 0 / 0.25)"} strokeWidth="1" />;
            })}
          </svg>
          <span className="absolute left-1/2 top-7 -translate-x-1/2 rounded-full bg-primary/30 px-2 py-0.5 text-[10px] text-primary">157 cm</span>
        </div>
      </div>
      <div className="mt-6 border-t border-white/5 pt-4">
        <div className="text-xs text-muted-foreground">Médecin traitant</div>
        <div className="mt-2 flex items-center gap-2">
          <img src="https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?auto=format&fit=crop&w=80&q=60" alt="" className="h-8 w-8 rounded-full object-cover" />
          <div className="flex-1 text-sm font-medium">Dr. Richard</div>
          <button className="inline-flex items-center gap-1 rounded-full bg-accent px-3 py-1 text-[10px] font-medium text-accent-foreground">
            <MessageSquare className="h-3 w-3" /> Chat
          </button>
        </div>
      </div>
      <div className="mt-4">
        <div className="text-xs text-muted-foreground">Personne à prévenir</div>
        <div className="mt-2 grid grid-cols-2 gap-2 rounded-xl bg-white/[0.03] p-3 text-xs">
          <div><div className="text-muted-foreground text-[10px]">Nom complet</div>Chloé Harrison (Sœur)</div>
          <div><div className="text-muted-foreground text-[10px]">Téléphone</div>+33 6 12 66 58 75</div>
          <div className="col-span-2"><div className="text-muted-foreground text-[10px]">Adresse</div>24 rue Greenfield, Riverside</div>
          <div><div className="text-muted-foreground text-[10px]">Ville</div>Paris</div>
          <div><div className="text-muted-foreground text-[10px]">Pays</div>France</div>
        </div>
      </div>
      <div className="mt-4">
        <div className="text-xs text-muted-foreground">Diagnostic santé mentale</div>
        <div className="mt-2 rounded-xl bg-white/[0.03] p-3 text-xs">
          <div className="flex items-center justify-between">
            <span className="font-medium">Trouble anxieux généralisé</span>
            <span className="rounded-full bg-success/20 px-2 py-0.5 text-[10px] text-success">Primaire</span>
          </div>
          <div className="mt-2 text-muted-foreground">Diagnostiqué le : 14 mai 2024</div>
          <div className="mt-1">Sévérité : <span className="rounded-full bg-accent/20 px-2 py-0.5 text-[10px] text-accent">Léger / Modéré</span></div>
        </div>
      </div>
    </div>
  );
}

function BloodRecord() {
  return (
    <div className="card-surface rounded-2xl p-5">
      <CardHeader title="Bilan sanguin" action="yellow" />
      <div className="mt-3 flex items-center justify-between gap-3 rounded-xl bg-success/10 p-3 text-xs">
        <span>Bonne nouvelle ! Votre taux sanguin est dans les normes.</span>
        <span className="rounded-full bg-success px-2 py-0.5 text-[10px] font-medium text-background">Excellent</span>
      </div>
      <div className="mt-4 grid grid-cols-[140px_1fr] items-center gap-4">
        <div className="relative">
          <svg viewBox="0 0 100 60" className="w-full">
            <defs>
              <linearGradient id="gauge" x1="0" x2="1">
                <stop offset="0%" stopColor="oklch(0.7 0.2 35)" />
                <stop offset="50%" stopColor="oklch(0.85 0.18 85)" />
                <stop offset="100%" stopColor="oklch(0.78 0.18 150)" />
              </linearGradient>
            </defs>
            <path d="M10 55 A 40 40 0 0 1 90 55" stroke="oklch(1 0 0 / 0.08)" strokeWidth="9" fill="none" strokeLinecap="round" />
            <path d="M10 55 A 40 40 0 0 1 78 24" stroke="url(#gauge)" strokeWidth="9" fill="none" strokeLinecap="round" />
            <circle cx="78" cy="24" r="4" fill="oklch(0.78 0.18 150)" stroke="oklch(0.16 0.005 240)" strokeWidth="2" />
          </svg>
          <div className="absolute inset-x-0 bottom-1 text-center">
            <div className="text-[10px] text-muted-foreground">Moyenne</div>
            <div className="text-xl font-light">132<span className="ml-0.5 text-[10px] text-muted-foreground">mg/dL</span></div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 text-xs">
          {[
            ["Hémoglobine", "13.8 g/dL"],
            ["Globules rouges", "4.7 M/µL"],
            ["Globules blancs", "6 200 /µL"],
            ["Plaquettes", "240 000 /µL"],
          ].map(([k, v]) => (
            <div key={k}><div className="text-muted-foreground">{k}</div><div className="text-sm font-medium">{v}</div></div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PulseRate() {
  return (
    <div className="card-surface rounded-2xl p-5">
      <CardHeader title="Rythme cardiaque" />
      <div className="relative mt-3 h-32">
        <svg viewBox="0 0 300 100" className="absolute inset-0 h-full w-full">
          <path d="M0 70 L40 65 L60 30 L80 70 L120 60 L140 75 L160 35 L180 70 L220 55 L240 80 L260 40 L300 60" fill="none" stroke="oklch(0.7 0.2 35)" strokeWidth="1.5" />
          <path d="M0 70 L40 65 L60 30 L80 70 L120 60 L140 75 L160 35 L180 70 L220 55 L240 80 L260 40 L300 60 L300 100 L0 100 Z" fill="oklch(0.7 0.2 35 / 0.12)" />
        </svg>
        <div className="absolute left-[45%] top-2 rounded-lg bg-card/95 px-2 py-1 text-[10px] shadow-lg ring-1 ring-white/10">
          <div className="text-muted-foreground">08:12</div>
          <div className="font-medium">54 BPM</div>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between text-xs">
        <span className="text-muted-foreground">Battements par minute</span>
        <span className="font-medium">60</span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
        <div>
          <div className="text-muted-foreground">Indicateur de stress</div>
          <span className="mt-1 inline-block chip" style={{ background: "oklch(0.7 0.2 35 / 0.15)", color: "oklch(0.7 0.2 35)" }}>Léger</span>
        </div>
        <div>
          <div className="text-muted-foreground">Qualité du sommeil</div>
          <span className="mt-1 inline-block chip" style={{ background: "oklch(0.78 0.18 150 / 0.15)", color: "oklch(0.78 0.18 150)" }}>Bonne</span>
        </div>
      </div>
      <div className="mt-3">
        <div className="text-xs text-muted-foreground">Calibration d'activité</div>
        <div className="text-sm font-medium">Retour à la normale en 4 minutes.</div>
        <div className="relative mt-2 h-1.5 rounded-full bg-gradient-to-r from-[oklch(0.7_0.2_35)] via-[oklch(0.85_0.18_85)] to-[oklch(0.78_0.18_150)]">
          <div className="absolute -top-1 h-3.5 w-3.5 -translate-x-1/2 rounded-full border-2 border-background bg-foreground" style={{ left: "80%" }} />
        </div>
      </div>
    </div>
  );
}

function Cholesterol() {
  return (
    <div className="card-surface rounded-2xl p-5">
      <CardHeader title="Cholestérol" />
      <div className="mt-3 text-xs">Cholestérol total : <span className="font-semibold">198 mg/dL</span></div>
      <div className="relative mt-2 h-1.5 rounded-full bg-gradient-to-r from-[oklch(0.78_0.18_150)] via-[oklch(0.85_0.18_85)] to-[oklch(0.7_0.2_35)]" />
      <div className="mt-1 flex justify-between text-[10px] text-muted-foreground"><span>40</span><span>100</span><span>140</span><span>180</span><span>240</span></div>
      <div className="mt-4 grid grid-cols-2 gap-4">
        <div>
          <div className="flex items-baseline gap-2"><span className="text-xs text-muted-foreground">HDL</span><span className="rounded-full bg-success/20 px-1.5 text-[10px] text-success">+5%</span></div>
          <div className="text-sm font-medium">54 mg/dL</div>
          <svg viewBox="0 0 100 30" className="mt-1 h-7 w-full"><path d="M0 20 L20 18 L35 10 L50 20 L70 8 L100 14" stroke="oklch(0.78 0.18 150)" fill="none" strokeWidth="1.5" /></svg>
        </div>
        <div>
          <div className="flex items-baseline gap-2"><span className="text-xs text-muted-foreground">LDL</span><span className="rounded-full bg-accent/20 px-1.5 text-[10px] text-accent">-3%</span></div>
          <div className="text-sm font-medium">126 mg/dL</div>
          <svg viewBox="0 0 100 30" className="mt-1 h-7 w-full"><path d="M0 10 L20 16 L35 8 L50 22 L70 14 L100 24" stroke="oklch(0.7 0.2 35)" fill="none" strokeWidth="1.5" /></svg>
        </div>
      </div>
    </div>
  );
}

export default function PatientsView() {
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <div className="space-y-4">
        <MyDocuments />
        <BloodRecord />
        <Cholesterol />
      </div>
      <div className="space-y-4">
        <ThreeDCheck />
        <PulseRate />
      </div>
      <div>
        <PersonalData />
      </div>
    </div>
  );
}
